<template>
  <div class="additional-ancillaries">
    <content-section :title="title" :description="description">
      <div
        v-for="segment in segment_groups"
        :key="`${step_id}:${segment.segmentId}`"
        class="additional-ancillaries__segment"
      >
        <div
          v-if="!group_ancillaries"
          class="additional-ancillaries__segment-label"
        >
          {{ segment_label(segment.segmentId) }}
        </div>
        <div
          v-for="traveler in segment.travelers"
          :key="`${step_id}:${segment.segmentId}:${traveler.id}`"
          class="additional-ancillaries__traveler-block"
        >
          <!-- Only a single picker and nothing else: the passenger name and
               the dropdown share one row, no repeated step title. -->
          <div
            v-if="is_picker_only(traveler)"
            class="additional-ancillaries__group-row"
          >
            <traveler-label :traveler_info="traveler" use_name />
            <selector
              :selected="
                selected_in_group(traveler.id, traveler.exclusive_groups[0])
              "
              :options="options_for_group(traveler.exclusive_groups[0])"
              :on-change="
                (value) =>
                  choose_exclusive(
                    traveler.id,
                    traveler.exclusive_groups[0],
                    value
                  )
              "
              :extra_style="selector_style"
              button_classes="rounded-full"
              show-dividers
            />
          </div>

          <template v-else>
            <div class="additional-ancillaries__traveler">
              <traveler-label :traveler_info="traveler" use_name />
            </div>

            <!-- Mutually-exclusive options collapse into one dropdown row —
                 picking one clears the rest (e.g. Wizzair checked bags). -->
            <div
              v-for="(group, group_index) in traveler.exclusive_groups"
              :key="`${traveler.id}:group:${group_index}`"
              class="additional-ancillaries__group-row"
            >
              <span class="additional-ancillaries__group-label">
                {{ step_name }}
              </span>
              <selector
                :selected="selected_in_group(traveler.id, group)"
                :options="options_for_group(group)"
                :on-change="
                  (value) => choose_exclusive(traveler.id, group, value)
                "
                :extra_style="selector_style"
                button_classes="rounded-full"
                show-dividers
              />
            </div>

            <!-- Stacking services keep an independent boolean switch each. -->
            <div
              v-if="traveler.toggles.length"
              class="additional-ancillaries__options"
            >
              <ancillary-option
                v-for="item in traveler.toggles"
                :key="item.compound_key"
                :title="item.title"
                :subtitle="item.subtitle"
                :price_amount="item.priceAmount"
                :currency_symbol="item.currencySymbol"
                :icon="item.icon"
                :selected="is_picked(traveler.id, item)"
                @select="pick(traveler.id, item)"
              />
            </div>
          </template>
        </div>
      </div>
    </content-section>
  </div>
</template>

<script>
  import AncillaryOption from './ancillary-option.vue';
  import Selector from '@liveflight/components/blocks/selector.vue';
  import TravelerLabel from '@liveflight/components/blocks/traveler/traveler-label.vue';
  import ContentSection from '@liveflight/components/blocks/wrappers/content-section.vue';
  import { get_config } from '@/utilities/commons';
  import {
    formatSegmentLabel,
    get_value_by_segment,
    offer_carrier_iata,
  } from '@liveflight/utilities/mixins';
  import { getSegmentsFromFlightOffers } from '@liveflight/utilities/seat-map';
  import {
    get_trip_type_from_itineraries,
    TRIP_TYPES,
  } from '@liveflight/utilities/origin-destination';
  import {
    group_catalog_by_segment_and_traveler,
    group_catalog_by_traveler,
    build_selector_options,
    update_traveler_selection,
    apply_pick_selection,
    apply_clear_selection,
    UNSET_ANCILLARY_SELECTION,
  } from '@liveflight/utilities/additional-services';

  // Generic ancillary picker, grouped Segment → Passenger. Within each
  // passenger, mutually-exclusive options (those linked by `incompatibleWith`
  // on the catalog entry) collapse into a single-select dropdown row; every
  // other option stacks as an independent boolean switch. Bundled picks still
  // follow `combineWith`.
  export default {
    name: 'additional-ancillaries',
    components: {
      'traveler-label': TravelerLabel,
      'ancillary-option': AncillaryOption,
      'content-section': ContentSection,
      selector: Selector,
    },
    props: {
      catalog: {
        type: Object,
        required: true,
      },
      travelers: {
        type: Array,
        required: true,
      },
      flight_offer: {
        type: Object,
        required: true,
      },
      value: {
        type: Object,
        default: () => ({}),
      },
      default_icon: {
        type: String,
        default: 'luggage',
      },
      step_id: {
        type: String,
        default: 'ancillary',
      },
      step_name: {
        type: String,
        default: '',
      },
      none_label: {
        type: String,
        default: 'Nessuna selezione',
      },
      description: String,
      title: String,
    },
    computed: {
      // Brand toggle: when on, per-segment rows collapse into one journey-wide
      // choice per product (booking stays segment-keyed under the hood).
      group_ancillaries() {
        return get_config('guiSettings.customizations.groupAncillaries', false);
      },
      segment_groups() {
        const builder = this.group_ancillaries
          ? group_catalog_by_traveler
          : group_catalog_by_segment_and_traveler;
        return builder(
          this.catalog,
          this.travelers,
          this.flight_offer,
          this.default_icon
        );
      },
      segments_map() {
        return getSegmentsFromFlightOffers([this.flight_offer]);
      },
      selector_style() {
        return { '--button-min-width': '260px' };
      },
    },
    methods: {
      // A row can cover a whole itinerary (Amadeus), so the section spans
      // from its first segment to the last one its row covers. Kyte rows
      // cover one segment each, so first and last coincide.
      segment_label(segment_id) {
        const carrier = offer_carrier_iata(this.flight_offer);
        const { segmentIds = [] } =
          Object.values(this.catalog).find(
            (row) => ((row || {}).segmentIds || [])[0] === segment_id
          ) || {};
        const first = get_value_by_segment(this.segments_map, segment_id, carrier);
        const last =
          get_value_by_segment(
            this.segments_map,
            segmentIds[segmentIds.length - 1],
            carrier
          ) || first;
        if (!first) return segment_id;
        // A row over the whole journey would read origin › origin on a round trip.
        const { itineraries = [] } = this.flight_offer;
        const journey_wide =
          itineraries.length > 1 &&
          itineraries.every(({ segments = [] }) =>
            segments.every(({ id }) => segmentIds.includes(id))
          );
        if (journey_wide) {
          return get_trip_type_from_itineraries(itineraries) === TRIP_TYPES.ROUND_TRIP
            ? 'Andata e ritorno'
            : 'Tutte le tratte';
        }
        return formatSegmentLabel(
          { departure: first.departure, arrival: last.arrival },
          true
        );
      },
      // True only when the item has member keys AND every one is set. The
      // length guard matters: [].every() is true, so an item with no member
      // keys would otherwise read as permanently picked.
      all_member_keys_selected(per_traveler, item) {
        const member_keys = (item || {}).member_keys || [];
        return (
          member_keys.length > 0 &&
          member_keys.every((key) => Number(per_traveler[key]) > 0)
        );
      },
      // An item is picked when every one of its member segment-keys is set.
      // In separated mode member_keys is a single key; in grouped mode it is
      // the whole journey's per-segment keys for the product.
      is_picked(traveler_id, item) {
        const per_traveler = this.value[traveler_id] || {};
        return this.all_member_keys_selected(per_traveler, item);
      },
      selected_in_group(traveler_id, group) {
        const per_traveler = this.value[traveler_id] || {};
        const chosen = (group || []).find((item) =>
          this.all_member_keys_selected(per_traveler, item)
        );
        return chosen ? chosen.compound_key : UNSET_ANCILLARY_SELECTION;
      },
      options_for_group(group) {
        return build_selector_options(group, this.none_label);
      },
      // A traveler whose only input is a single picker (no stacking toggles):
      // the dropdown sits on the passenger-name row instead of repeating the
      // step title on its own row.
      is_picker_only(traveler) {
        return (
          (traveler.exclusive_groups || []).length === 1 &&
          (traveler.toggles || []).length === 0
        );
      },
      // Single-select: clear every member key of every option in the group,
      // then apply the chosen option's member keys (each key's own
      // incompatibleWith / combineWith still resolve, scoped to its segment).
      // "None" just clears the group. Grouped mode spans every leg at once.
      choose_exclusive(traveler_id, group, value) {
        const items = group || [];
        const chosen = items.find((item) => item.compound_key === value);
        this.$emit(
          'input',
          update_traveler_selection(this.value, traveler_id, (per_traveler) => {
            items.forEach((item) =>
              (item.member_keys || []).forEach((key) =>
                apply_clear_selection(this.catalog, per_traveler, key)
              )
            );
            if (chosen && value !== UNSET_ANCILLARY_SELECTION) {
              chosen.member_keys.forEach((key) =>
                apply_pick_selection(this.catalog, per_traveler, key)
              );
            }
          })
        );
      },
      pick(traveler_id, item) {
        const member_keys = item.member_keys || [];
        this.$emit(
          'input',
          update_traveler_selection(this.value, traveler_id, (per_traveler) => {
            const already_picked = this.all_member_keys_selected(
              per_traveler,
              item
            );
            member_keys.forEach((key) => {
              if (already_picked) {
                apply_clear_selection(this.catalog, per_traveler, key);
              } else {
                apply_pick_selection(this.catalog, per_traveler, key);
              }
            });
          })
        );
      },
    },
  };
</script>

<style lang="scss" scoped>
  .additional-ancillaries {
    &__segment {
      display: flex;
      flex-direction: column;
      gap: 0;
      padding-top: 12px;
      padding-bottom: 12px;

      &:not(:last-child) {
        border-bottom: 1px solid $gray-300;
      }
    }

    &__segment-label {
      font-weight: $vb-font-weight-semibold;
      color: $gray-700;
      padding-bottom: 8px;
    }

    &__traveler-block {
      display: flex;
      flex-direction: column;
      gap: 12px;

      & + & {
        padding-top: 16px;
      }
    }

    &__traveler {
      display: flex;
      gap: 8px;
    }

    &__group-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 16px;
      flex-wrap: wrap;
    }

    &__group-label {
      font-size: $font-size-md;
      line-height: 1;
      flex-grow: 1;
    }

    &__options {
      display: flex;
      flex-direction: column;
      gap: 5px;
    }
  }
</style>
